import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosResultDefaultError } from '../../../api/request';
import {
  getPeriodsByPeriodRequest,
  getPeriodsByTypeRequest,
  Period,
} from '../../../api/requests/periods';
import { AuthenticationError } from '../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';

export interface PeriodsLoaderProps {
  periodTypeId?: number | string;
  children: (periods: Period[]) => JSX.Element;
}

const { FetchProvider, FetchConsumer } = createFetchContext<
  undefined,
  Period[],
  AuthenticationError | AxiosResultDefaultError
>();

export const PeriodsByTypeLoaderConsumer = FetchConsumer;

export default function PeriodsByTypeLoader({
  periodTypeId,
  children,
}: PeriodsLoaderProps) {
  const { id } = useParams<{ id: string }>();

  const request = useCallback(async () => {
    if (periodTypeId) {
      return getPeriodsByTypeRequest(periodTypeId);
    }
    return getPeriodsByPeriodRequest(id);
  }, [periodTypeId, id]);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return <Loading insideCard />;
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children(data);
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
